import { memo } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import { Close as CloseIcon } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'

import { stylePropType } from '~/src/Lib/PropTypes'
import getClassPrefixer from '~/src/UI/classPrefixer'

const displayName = 'CloseButton'
const classes = getClassPrefixer(displayName)

const RootIconButton = styled(IconButton, { name: displayName })(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(),
  right: theme.spacing(),
  transform: 'scale(0.75)',
  transformOrigin: 'top right',
  pointerEvents: 'auto',
  [`&.${classes.noMargin}`]: {
    top: 0,
    right: 0,
  },
  [`&.${classes.defaultColor}`]: {
    color: theme.palette.monochrome?.[10] ?? theme.palette.text.primary,
  },
}))

/**
 * A generic close button that positions itself in the top right corner of any position: relative container
 *
 * @example
 *   ;<Box
 *     display="grid"
 *     gridTemplateColumns="repeat(2, 1fr)"
 *     gridTemplateRows="repeat(2, 1fr)"
 *     gap="1rem"
 *     width="100%"
 *     height="25rem"
 *   >
 *     <Box border={1} borderColor="primary.main" position="relative" width="100%" height="100%">
 *       <CloseButton />
 *     </Box>
 *     <Box border={1} borderColor="primary.main" position="relative" width="100%" height="100%">
 *       <CloseButton noMargin color="primary" />
 *     </Box>
 *     <Box border={1} borderColor="currentColor" color="error.main" position="relative" width="100%" height="100%">
 *       <CloseButton noMargin color="inherit" />
 *     </Box>
 *     <Box border={1} borderColor="primary.main" position="relative" width="100%" height="100%">
 *       <CloseButton color="secondary" />
 *     </Box>
 *   </Box>
 *
 * @alias CloseButton
 * @component
 * @named_export CloseButtonComponent
 */
export const CloseButtonComponent = ({
  color = 'default',
  onClose,
  style = undefined,
  noMargin = false,
  'aria-label': ariaLabel = 'AROYA dialog close button',
}) => (
  <RootIconButton
    aria-label={ariaLabel}
    className={classNames({
      [classes.noMargin]: noMargin,
      [classes.defaultColor]: color === 'default',
    })}
    color={color === 'default' ? undefined : color}
    data-testid="aroya-close-button"
    onClick={onClose}
    style={style}
    size="large"
  >
    <CloseIcon color="inherit" />
  </RootIconButton>
)
CloseButtonComponent.displayName = 'CloseButton'
CloseButtonComponent.propTypes = {
  'aria-label': PropTypes.string,
  color: PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary']),
  noMargin: PropTypes.bool,
  style: stylePropType,
  onClose: PropTypes.func.isRequired,
}

export default memo(CloseButtonComponent)
